/**
 * Prompt 版本管理 API client
 *
 * Phase 3-2 / Plan §5.5 B5 / Design §8.2 prompt 权限点。
 *
 * P0-1 修复：DTO 字段对齐 content-pipeline schema（version/active/BIGINT epoch ms）。
 *   删除: versionLabel / isActive / activatedAt / activatedBy / variables / notes
 *   新增: version / active / createdAt(number|null) / updatedAt(number|null)
 * P2 修复：broadcastError 统一为 null（非 undefined）。
 *
 * 包装 axios apiClient 调用 admin-service /prompts 端点；
 * 返回类型与 admin-service rowToDto + activate 路由返回形状对齐。
 */
import { apiClient } from './client';

// ── DTO 类型定义 ─────────────────────────────────────────────────────────────

/** Prompt 版本 DTO（与 admin-service rowToDto 1:1 对齐，P0-1 字段名修正） */
export interface PromptVersionDto {
  id: string;
  taskType: string;
  /** 版本号（原 versionLabel，对齐 content-pipeline schema） */
  version: string;
  content: string;
  /** 是否激活（原 isActive，TINYINT active = 1 时为 true） */
  active: boolean;
  /** 创建时间：epoch ms（BIGINT created_at），null 表示 DB 返回异常 */
  createdAt: number | null;
  /** 更新时间：epoch ms（BIGINT updated_at），null 表示 DB 返回异常 */
  updatedAt: number | null;
  createdBy: string | null;
}

/** 列表过滤参数（P0-1: isActive → active） */
export interface ListPromptVersionsFilter {
  taskType?: string;
  active?: boolean;
  limit?: number;
  offset?: number;
}

/** GET / 列表响应 */
export interface ListPromptVersionsResponse {
  items: PromptVersionDto[];
  total: number;
  limit: number;
  offset: number;
}

/** POST / 请求 body（P0-1: versionLabel → version，删 variables/notes） */
export interface CreatePromptVersionInput {
  taskType: string;
  version: string;
  content: string;
}

/** POST /:id/activate 响应（P2: broadcastError 统一为 null | {...}） */
export interface ActivatePromptVersionResponse {
  taskType: string;
  activeVersionId: string;
  /** epoch-ms，若 content-pipeline 不可达则为 null（partial success） */
  broadcastAt: number | null;
  /** partial success 时有值；成功时为 null（P2: 统一 null，非 undefined） */
  broadcastError: { code: number; message: string } | null;
}

/** GET /:id/diff/:otherId 响应（P0-1: version 字段） */
export interface PromptDiffResponse {
  versionA: {
    id: string;
    version: string;
    taskType: string;
    content: string;
  };
  versionB: {
    id: string;
    version: string;
    taskType: string;
    content: string;
  };
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

// ── API 函数 ─────────────────────────────────────────────────────────────────

/**
 * GET /prompts — 版本列表（分页 + 过滤）
 * P0-1 注：list 返回的 content 在 admin-service 截断至 200 字符。
 */
export async function listPromptVersions(
  filter: ListPromptVersionsFilter = {},
): Promise<ListPromptVersionsResponse> {
  const params: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== '') {
      params[k] = v as string | number | boolean;
    }
  }
  const resp = await apiClient.get<ServiceResponse<ListPromptVersionsResponse>>(
    '/prompts',
    { params },
  );
  return resp.data.data;
}

/**
 * GET /prompts/task-types — 支持的 task_type 枚举（前端 Select 用）
 */
export async function listTaskTypes(): Promise<string[]> {
  const resp = await apiClient.get<ServiceResponse<string[]>>('/prompts/task-types');
  return resp.data.data;
}

/**
 * GET /prompts/:id — 单版本详情（含 content 全文，不截断）
 */
export async function getPromptVersion(id: string): Promise<PromptVersionDto> {
  const resp = await apiClient.get<ServiceResponse<PromptVersionDto>>(
    `/prompts/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/**
 * GET /prompts/:id/diff/:otherId — 两版本内容对比（side-by-side）
 * P2 注：两版本必须属于同一 task_type，否则 admin-service 返回 400。
 */
export async function getPromptDiff(idA: string, idB: string): Promise<PromptDiffResponse> {
  const resp = await apiClient.get<ServiceResponse<PromptDiffResponse>>(
    `/prompts/${encodeURIComponent(idA)}/diff/${encodeURIComponent(idB)}`,
  );
  return resp.data.data;
}

/**
 * POST /prompts — 创建新版本（active 默认 false）
 * P0-1: body 用 version（非 versionLabel），删 variables/notes。
 * P2: content 限制 1..60000 字符（admin-service 校验，超限返回 400）。
 */
export async function createPromptVersion(
  input: CreatePromptVersionInput,
): Promise<PromptVersionDto> {
  const resp = await apiClient.post<ServiceResponse<PromptVersionDto>>(
    '/prompts',
    input,
  );
  return resp.data.data;
}

/**
 * POST /prompts/:id/activate — 激活版本（触发 content-pipeline 热重载，约 30s 内生效）
 * broadcastError 不为 null 时为 partial success（admin 表已写入，pipeline 报错）。
 */
export async function activatePromptVersion(
  id: string,
): Promise<ActivatePromptVersionResponse> {
  const resp = await apiClient.post<ServiceResponse<ActivatePromptVersionResponse>>(
    `/prompts/${encodeURIComponent(id)}/activate`,
    {},
  );
  return resp.data.data;
}

/**
 * POST /prompts/task-types/:taskType/rollback — 回滚到上一个非 active 版本
 * P0-1 注：上一版定义为 created_at DESC、非 active、非当前版本（无 activated_at 字段）。
 */
export async function rollbackPromptVersion(
  taskType: string,
): Promise<ActivatePromptVersionResponse> {
  const resp = await apiClient.post<ServiceResponse<ActivatePromptVersionResponse>>(
    `/prompts/task-types/${encodeURIComponent(taskType)}/rollback`,
    {},
  );
  return resp.data.data;
}
